import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/img/logo.png";
import { useQuery } from "@tanstack/react-query";
import { AuthContext } from "../context/AuthProvider";
import { CartContext } from "../context/CartProvider";
import { WishListContext } from "../context/WishListProvider";
import { FaShoppingCart, FaRegHeart, FaSignOutAlt, FaBars, FaTimes, FaUserCircle } from "react-icons/fa";
import { PiUserCircleFill } from "react-icons/pi";
import { motion, AnimatePresence } from "framer-motion";
import "./header.css";

const Header = () => {
  const { cart } = useContext(CartContext);
  const { wishList } = useContext(WishListContext);
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);

  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api/";

  const fetchCategories = async () => {
    const res = await fetch(`${baseUrl}catagorylist`);
    if (!res.ok) throw new Error("Failed to fetch categories");
    const data = await res.json();
    return data.catagories || data.data || [];
  };

  const { data: categories = [] } = useQuery({
    queryKey: ["categories-list-header"],
    queryFn: fetchCategories,
  });

  const handleLogout = () => {
    logout();
    setIsUserDropdownOpen(false);
    setIsDrawerOpen(false);
    navigate("/");
  };

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);
  const closeDrawer = () => setIsDrawerOpen(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <header className="main-header-wrapper">
        <div className="container main-header-container">
          {/* Mobile Layout */}
          <div className="d-lg-none w-100 d-flex align-items-center justify-content-between">
            <button className="navbar-toggler" onClick={toggleDrawer}>
              <FaBars />
            </button>
            <Link to="/" className="navbar-brand m-0" id="mob-logo-brand">
              <img src={logo} className="logo_img" style={{ height: '65px' }} alt="Logo" />
            </Link>
            <div className="d-flex align-items-center gap-2">
              <Link to="/wishlist" className="header-icon-btn position-relative">
                <FaRegHeart />
                {wishList.length > 0 && <span className="badge">{wishList.length}</span>}
              </Link>
              <Link to="/cart" className="header-icon-btn position-relative">
                <FaShoppingCart />
                {cart.length > 0 && <span className="badge">{cart.length}</span>}
              </Link>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="d-none d-lg-flex desktop-header-layout w-100 align-items-center justify-content-between">
            <Link to="/" className="navbar-brand">
              <img src={logo} className="logo_img" alt="Logo" />
            </Link>

            <ul className="navbar-nav mx-auto flex-row align-items-center">
              <li className="nav-item"><Link className="nav-link" to="/">Home</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/shop">Shop</Link></li>
              <li className="nav-item dropdown">
                <Link className="nav-link dropdown-toggle" to="#" data-bs-toggle="dropdown">
                  Categories
                </Link>
                <ul className="dropdown-menu">
                  {categories?.map((cat) => (
                    <li key={cat._id}><Link className="dropdown-item" to={`/category/${cat._id}`}>{cat.catagoryname}</Link></li>
                  ))}
                </ul>
              </li>
              <li className="nav-item"><Link className="nav-link" to="/blog">Blog</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/contact">Contact</Link></li>
            </ul>

            <div className="desktop-nav-actions">
              {user ? (
                <div className="position-relative" ref={userDropdownRef}>
                  <button className="header-icon-btn" onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}>
                    <PiUserCircleFill size={24} />
                  </button>
                  <AnimatePresence>
                    {isUserDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="user-dropdown-menu desktop-user-dropdown"
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          backgroundColor: 'white',
                          minWidth: '180px',
                          borderRadius: '15px',
                          padding: '10px',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                          zIndex: 1000
                        }}
                      >
                        <Link className="dropdown-item d-flex align-items-center gap-2" to="/myaccount" onClick={() => setIsUserDropdownOpen(false)}>
                          <PiUserCircleFill /> Profile
                        </Link>
                        <hr className="my-2" />
                        <button className="dropdown-item d-flex align-items-center gap-2 text-danger" onClick={handleLogout}>
                          <FaSignOutAlt /> Logout
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link to="/login" className="login-btn-header">
                  <FaUserCircle size={20} /> Login
                </Link>
              )}

              <Link to="/wishlist" className="header-icon-btn">
                <FaRegHeart />
                {wishList.length > 0 && <span className="badge">{wishList.length}</span>}
              </Link>

              <Link to="/cart" className="header-icon-btn cart-btn-premium">
                <FaShoppingCart />
                <span className="d-none d-xl-inline">Cart</span>
                {cart.length > 0 && <span className="badge">{cart.length}</span>}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isDrawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="drawer-overlay"
              onClick={toggleDrawer}
              style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1040, display: 'block' }}
            />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="nav-drawer"
              style={{ position: 'fixed', top: 0, left: 0, width: '280px', height: '100%', backgroundColor: 'white', zIndex: 1045, display: 'flex', flexDirection: 'column' }}
            >
              <div className="p-4 d-flex justify-content-between align-items-center border-bottom">
                <img src={logo} style={{ height: '40px' }} alt="Logo" />
                <button className="btn p-0 border-0" onClick={toggleDrawer}><FaTimes size={20} /></button>
              </div>
              <div className="flex-grow-1 overflow-auto p-3">
                <Link className="nav-link p-3 border-bottom d-block text-dark fw-bold" to="/" onClick={closeDrawer}>Home</Link>
                <Link className="nav-link p-3 border-bottom d-block text-dark fw-bold" to="/shop" onClick={closeDrawer}>Shop</Link>
                <div className="p-3 border-bottom">
                  <span className="text-muted small text-uppercase fw-bold text-decoration-none">Categories</span>
                  <div className="mt-2 ps-2">
                    {categories?.slice(0, 5).map(cat => (
                      <Link key={cat._id} className="d-block py-2 text-dark text-decoration-none" to={`/category/${cat._id}`} onClick={closeDrawer}>{cat.catagoryname}</Link>
                    ))}
                  </div>
                </div>
                <Link className="nav-link p-3 border-bottom d-block text-dark fw-bold text-decoration-none" to="/blog" onClick={closeDrawer}>Blog</Link>
                <Link className="nav-link p-3 border-bottom d-block text-dark fw-bold text-decoration-none" to="/contact" onClick={closeDrawer}>Contact</Link>
              </div>
              <div className="p-4 border-top">
                {user ? (
                  <button className="btn btn-outline-danger w-100 rounded-pill fw-bold" onClick={handleLogout}>Logout</button>
                ) : (
                  <Link to="/login" className="btn btn-danger w-100 rounded-pill fw-bold text-decoration-none" onClick={closeDrawer}>Login / Register</Link>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
